/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Date range preset definitions and computation logic for DOMO-style quick period selection.
 * @graph-connects none
 */
export type DateRangePreset =
  | 'thisMonth'
  | 'lastMonth'
  | 'last7d'
  | 'last30d'
  | 'last90d'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'thisYear'
  | 'lastYear'
  | 'all'
  | 'custom';

/** @graph-connects none */
export interface DateRangePresetOption {
  key: DateRangePreset;
  label: string;
}

/** @graph-connects none */
export const DATE_RANGE_PRESET_OPTIONS: readonly DateRangePresetOption[] = [
  { key: 'thisMonth', label: '今月' },
  { key: 'lastMonth', label: '前月' },
  { key: 'last7d', label: '過去7日' },
  { key: 'last30d', label: '過去30日' },
  { key: 'last90d', label: '過去90日' },
  { key: 'thisQuarter', label: '今四半期' },
  { key: 'lastQuarter', label: '前四半期' },
  { key: 'thisYear', label: '今年' },
  { key: 'lastYear', label: '前年' },
  { key: 'all', label: '全期間' },
  { key: 'custom', label: 'カスタム' },
];

/** @graph-connects none */
function formatDate(d: Date): string {
  const y = String(d.getFullYear());
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** @graph-connects none */
function getQuarterStart(year: number, quarter: number): Date {
  return new Date(year, (quarter - 1) * 3, 1);
}

/** @graph-connects none */
function getQuarterEnd(year: number, quarter: number): Date {
  return new Date(year, quarter * 3, 0);
}

/** @graph-connects none */
function getCurrentQuarter(d: Date): number {
  return Math.floor(d.getMonth() / 3) + 1;
}

/**
 * @graph-connects none
 */
export function computeDateRange(
  preset: DateRangePreset,
  today: Date,
  minDate?: string,
): { from: string; to: string } {
  const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  switch (preset) {
    case 'thisMonth': {
      const from = new Date(t.getFullYear(), t.getMonth(), 1);
      return { from: formatDate(from), to: formatDate(t) };
    }
    case 'lastMonth': {
      const from = new Date(t.getFullYear(), t.getMonth() - 1, 1);
      const to = new Date(t.getFullYear(), t.getMonth(), 0);
      return { from: formatDate(from), to: formatDate(to) };
    }
    case 'last7d': {
      const from = new Date(t);
      from.setDate(from.getDate() - 6);
      return { from: formatDate(from), to: formatDate(t) };
    }
    case 'last30d': {
      const from = new Date(t);
      from.setDate(from.getDate() - 29);
      return { from: formatDate(from), to: formatDate(t) };
    }
    case 'last90d': {
      const from = new Date(t);
      from.setDate(from.getDate() - 89);
      return { from: formatDate(from), to: formatDate(t) };
    }
    case 'thisQuarter': {
      const q = getCurrentQuarter(t);
      const from = getQuarterStart(t.getFullYear(), q);
      return { from: formatDate(from), to: formatDate(t) };
    }
    case 'lastQuarter': {
      let q = getCurrentQuarter(t) - 1;
      let year = t.getFullYear();
      if (q <= 0) {
        q = 4;
        year -= 1;
      }
      return {
        from: formatDate(getQuarterStart(year, q)),
        to: formatDate(getQuarterEnd(year, q)),
      };
    }
    case 'thisYear': {
      return { from: `${String(t.getFullYear())}-01-01`, to: formatDate(t) };
    }
    case 'lastYear': {
      const year = String(t.getFullYear() - 1);
      return { from: `${year}-01-01`, to: `${year}-12-31` };
    }
    case 'all': {
      return { from: minDate ?? '', to: '' };
    }
    case 'custom': {
      return { from: '', to: '' };
    }
  }
}

/**
 * @graph-connects computeDateRange [calls] compare against each preset range
 * @graph-connects DATE_RANGE_PRESET_OPTIONS [references] iterate preset list
 */
export function derivePreset(from: string, to: string, minDate?: string): DateRangePreset {
  const today = new Date();
  for (const opt of DATE_RANGE_PRESET_OPTIONS) {
    if (opt.key === 'custom') continue;
    const range = computeDateRange(opt.key, today, minDate);
    if (range.from === from && range.to === to) return opt.key;
  }
  return 'custom';
}
